import type { ReactNode } from "react";
import { AlertCircle, RefreshCw01 } from "@untitledui/icons";
import { Button } from "../components/base/buttons/button";
import { FeaturedIcon } from "../components/foundations/featured-icon/featured-icon";
import { cx } from "../utils/cx";
import { PageSkeleton } from "./Skeleton";

/** The minimal slice of a TanStack Query result this component needs. */
export interface QueryLike<T> {
    data: T | undefined;
    isPending: boolean;
    isError: boolean;
    error: unknown;
    refetch: () => unknown;
}

/** Persian message from an API error (`{ message }` bodies) or a generic one. */
export function errorMessage(error: unknown): string {
    if (error && typeof error === "object" && "message" in error && typeof (error as { message: unknown }).message === "string") {
        const m = (error as { message: string }).message;
        if (/[؀-ۿ]/.test(m)) return m;
    }
    return "ارتباط با سرور برقرار نشد. اتصال اینترنت را بررسی کنید و دوباره تلاش کنید.";
}

export const ErrorPanel = ({ error, onRetry, className }: { error: unknown; onRetry?: () => void; className?: string }) => (
    <div
        role="alert"
        className={cx("flex flex-col items-center gap-4 rounded-2xl bg-primary px-6 py-10 text-center shadow-xs ring-1 ring-secondary ring-inset", className)}
    >
        <FeaturedIcon icon={AlertCircle} color="error" theme="light" size="lg" />
        <div className="flex max-w-md flex-col gap-1">
            <p className="text-md font-semibold text-primary">این بخش بارگذاری نشد</p>
            <p className="text-sm text-tertiary">{errorMessage(error)}</p>
        </div>
        {onRetry && (
            <Button color="secondary" size="md" iconLeading={RefreshCw01} onPress={onRetry}>
                تلاش دوباره
            </Button>
        )}
    </div>
);

/**
 * Loading → skeleton, error → explained retry panel, data → children.
 * `<QueryState query={q} skeleton={<SkeletonTable />}>{(data) => …}</QueryState>`
 */
export function QueryState<T>({ query, skeleton, children }: { query: QueryLike<T>; skeleton?: ReactNode; children: (data: T) => ReactNode }) {
    if (query.isPending) return <>{skeleton ?? <PageSkeleton />}</>;
    if (query.isError || query.data === undefined) return <ErrorPanel error={query.error} onRetry={() => void query.refetch()} />;
    return <>{children(query.data)}</>;
}
